import { getAxiosInstance } from './axiosConfig';
import { assertApiPath, type ApiPath } from './apiPath';
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

/** URL and origin are selected by the path argument and the configured API client, respectively. */
export type ApiRequestConfig = Omit<AxiosRequestConfig, 'url' | 'baseURL' | 'allowAbsoluteUrls'> & {
  url?: never;
  baseURL?: never;
  allowAbsoluteUrls?: never;
};

/** Preserve Axios response types while requiring safely constructed paths at every request entry point. */
export type SafeAxiosInstance = {
  // Axios defaults responses to any; retain that contract for existing untyped endpoints.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  get<T = any, R = AxiosResponse<T>>(path: ApiPath, config?: ApiRequestConfig): Promise<R>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  delete<T = any, R = AxiosResponse<T>>(path: ApiPath, config?: ApiRequestConfig): Promise<R>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  head<T = any, R = AxiosResponse<T>>(path: ApiPath, config?: ApiRequestConfig): Promise<R>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  options<T = any, R = AxiosResponse<T>>(path: ApiPath, config?: ApiRequestConfig): Promise<R>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  post<T = any, R = AxiosResponse<T>>(path: ApiPath, data?: unknown, config?: ApiRequestConfig): Promise<R>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  put<T = any, R = AxiosResponse<T>>(path: ApiPath, data?: unknown, config?: ApiRequestConfig): Promise<R>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  patch<T = any, R = AxiosResponse<T>>(path: ApiPath, data?: unknown, config?: ApiRequestConfig): Promise<R>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  request<T = any, R = AxiosResponse<T>>(config: Omit<ApiRequestConfig, 'url'> & { url: ApiPath }): Promise<R>;
};

/** Leave auth, retries and WAF recovery on the original instance; constrain its public request surface. */
export function createSafeAxios(getTransport: () => AxiosInstance): SafeAxiosInstance {
  const check = (path: ApiPath, config?: ApiRequestConfig) => {
    assertApiPath(path);
    if (config && ('baseURL' in config || 'allowAbsoluteUrls' in config || 'url' in config)) {
      throw new TypeError('API request configuration cannot override its URL');
    }
  };
  return {
    get: async (path, ...rest) => {
      check(path, rest[0]);
      return getTransport().get(path, ...rest);
    },
    delete: async (path, ...rest) => {
      check(path, rest[0]);
      return getTransport().delete(path, ...rest);
    },
    head: async (path, ...rest) => {
      check(path, rest[0]);
      return getTransport().head(path, ...rest);
    },
    options: async (path, ...rest) => {
      check(path, rest[0]);
      return getTransport().options(path, ...rest);
    },
    post: async (path, ...rest) => {
      check(path, rest[1]);
      return getTransport().post(path, ...rest);
    },
    put: async (path, ...rest) => {
      check(path, rest[1]);
      return getTransport().put(path, ...rest);
    },
    patch: async (path, ...rest) => {
      check(path, rest[1]);
      return getTransport().patch(path, ...rest);
    },
    request: async ({ url: path, ...config }) => {
      check(path, config);
      return getTransport().request({ ...config, url: path });
    },
  };
}

/** Application requests use this facade; raw defaults and interceptors stay inside trusted transport modules. */
export const safeAxios = createSafeAxios(getAxiosInstance);
